import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Archive, Star, CheckCircle2, ExternalLink } from "lucide-react";
import { PageHeader, StatusBadge, fmtDate, Empty } from "../../components/admin/ui";

const LatestServices = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/media/latest-services");
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const publish = async (id) => {
    try { await api.post(`/admin/media/${id}/publish`); toast.success("Promoted to Latest. Older entries beyond cap moved to archive."); load(); }
    catch { toast.error("Failed."); }
  };
  const archive = async (id) => {
    try { await api.post(`/admin/media/${id}/archive`); toast.success("Archived."); load(); }
    catch { toast.error("Failed."); }
  };

  if (loading || !data) return <div className="text-[#D9D9D9]/60"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="space-y-8" data-testid="admin-latest-services">
      <PageHeader
        overline="Latest Services"
        title={<>The <span className="italic gold-text">five</span> on the altar</>}
        subtitle={`Only the ${data.cap} latest published services appear on the public site. Older services are archived, never deleted.`}
      />

      <section data-testid="latest-list">
        <div className="overline mb-3 flex items-center gap-2"><Star className="h-4 w-4 text-[#C9A44C]" /> Currently Displayed ({data.latest.length}/{data.cap})</div>
        {data.latest.length === 0 ? (
          <Empty title="No services on the altar yet" subtitle="Add a recording and click 'Publish & Mark Latest' to put it on the public site." />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {data.latest.map((r, i) => (
              <article key={r.id} className="bento-card !p-6" data-testid={`latest-${r.id}`}>
                <span className="glow-blob" />
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">{r.service_type} · #{i + 1}</div>
                    <h3 className="font-serif-display text-2xl text-[#F6F0E6] mt-1 truncate">{r.title}</h3>
                    <p className="text-xs text-[#D9D9D9]/70 mt-1">{r.speaker} · {fmtDate(r.service_date || r.date)}</p>
                  </div>
                  <StatusBadge status="published" />
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <a href={r.link} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2 !px-3 !text-xs"><ExternalLink className="h-3.5 w-3.5" /> Open</a>
                  <button onClick={() => archive(r.id)} className="btn-ghost !py-2 !px-3 !text-xs"><Archive className="h-3.5 w-3.5" /> Remove from Latest</button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section data-testid="archived-list">
        <div className="overline mb-3 flex items-center gap-2"><Archive className="h-4 w-4 text-[#D9D9D9]/60" /> Archive · still safe on Drive</div>
        {data.archived.length === 0 ? (
          <p className="text-sm text-[#D9D9D9]/60">No archived services yet. Older services move here automatically once the {data.cap}-cap is exceeded.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.archived.map((r) => (
              <article key={r.id} className="bento-card !p-5" data-testid={`archived-${r.id}`}>
                <span className="glow-blob" />
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]/70">{r.service_type}</div>
                <h3 className="font-serif-display text-xl text-[#F6F0E6] mt-1 truncate">{r.title}</h3>
                <p className="text-xs text-[#D9D9D9]/60 mt-1">{r.speaker} · {fmtDate(r.service_date || r.date)}</p>
                <button onClick={() => publish(r.id)} className="btn-ghost !py-2 !px-3 !text-xs mt-3 border-[#C9A44C]/60 text-[#C9A44C]">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Restore to Latest
                </button>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default LatestServices;
